import React, { useState, Dispatch, SetStateAction } from 'react';
import useDisableBackSwipe from '@/hooks/useDisableBackSwipe';
import { ScoutLocation } from '@/types/location';
import Summary from '@/components/ui/scout/location/Summary';
import { Button } from '@/components/shadcn/button';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/shadcn/breadcrumb';
import { useScout } from '@/hooks/useScout';
import { capitalize } from '@/utils/strings';
import { Views } from '@/types/view';
import Detailed from './location/Detailed';

export interface LocationProps {
  activeLocation: ScoutLocation | undefined;
  activeView: Views;
  setActiveLocationId: Dispatch<SetStateAction<string | number | null>>;
  locations: ScoutLocation[];
  itemsPerPage?: number;
  selectionCallback?: (id: string | number, lastSelectedView: Views) => void;
  setActiveView: Dispatch<SetStateAction<Views>>;
  setBurstModalOptions: Dispatch<
    SetStateAction<{
      isOpen: boolean;
      locationsIds: string[];
      successCallback: () => void;
    }>
  >;
}

const Location: React.FC<LocationProps> = ({
  activeLocation,
  activeView,
  setActiveLocationId,
  locations,
  itemsPerPage = 10,
  selectionCallback = () => {},
  setActiveView,
  setBurstModalOptions,
}) => {
  const [pageIndex, setPageIndex] = useState(1);
  const { lastSelectedView } = useScout();

  useDisableBackSwipe();

  const currentLocations = locations.slice(
    (pageIndex - 1) * itemsPerPage,
    pageIndex * itemsPerPage,
  );

  const scrollToTarget = () => {
    document.documentElement.scrollTop = 0;
  };

  return activeLocation ? (
    <>
      <Breadcrumb>
        <BreadcrumbList className="mb-6">
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <div
                className="cursor-pointer"
                onClick={() => {
                  setActiveLocationId(null);
                  setActiveView(lastSelectedView);
                }}>
                {capitalize(lastSelectedView)}
              </div>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{activeLocation.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <Detailed
        location={activeLocation}
        activeView={activeView}
        setBurstModalOptions={setBurstModalOptions}
      />
    </>
  ) : (
    <div className="flex w-full flex-col gap-4">
      {currentLocations.map(location => {
        return (
          <Summary
            key={location._id}
            location={location}
            selectionCallback={selectionCallback}
            setBurstModalOptions={setBurstModalOptions}
          />
        );
      })}
      {locations.length > 0 ? (
        <>
          <div className="space-x-2 self-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setPageIndex(curr => Math.max(curr - 1, 0));
                scrollToTarget();
              }}
              disabled={pageIndex === 1}>
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setPageIndex(curr => curr + 1);
                scrollToTarget();
              }}
              disabled={pageIndex >= locations.length / itemsPerPage}>
              Next
            </Button>
          </div>
        </>
      ) : (
        <div className="flex h-[calc(100dvh-215px)] items-center justify-center">
          Press Upload to start monitoring locations with Scout.
        </div>
      )}
    </div>
  );
};

export default Location;
